/* Login page — temporary numeric-code mode.
 *
 * While the production DB is out of sync (Emergent Support ticket open),
 * the operator authenticates with a 4-6 digit numeric access code that
 * maps to the seeded admin user server-side. The Google OAuth entry
 * point is preserved as an escape hatch, but the primary flow is now
 * the code pad below.
 */
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BrandMark } from "@/components/BrandMark";
import { toast } from "sonner";
import { ArrowLeft, Lock, KeyRound } from "lucide-react";

const CODE_LEN = 6;

export default function LoginPage() {
  const { loginWithCode, user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [digits, setDigits] = useState(Array(CODE_LEN).fill(""));
  const [busy, setBusy] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    // Auto-focus first empty box on mount
    const idx = digits.findIndex((d) => !d);
    if (idx >= 0 && inputsRef.current[idx]) inputsRef.current[idx].focus();
  }, [digits]);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitCode = async (fullCode) => {
    setBusy(true);
    try {
      // Backend accepts 4-6 digits, so we submit the trimmed code (drop
      // trailing empty boxes).
      const code = fullCode.replace(/[^0-9]/g, "");
      if (code.length < 4) {
        toast.error("Código demasiado curto");
        setBusy(false);
        return;
      }
      await loginWithCode(code);
      toast.success("Bem-vindo de volta");
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : detail?.message) || "Código inválido");
      // Clear on failure so user can retype quickly
      setDigits(Array(CODE_LEN).fill(""));
      inputsRef.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const handleChange = (i, val) => {
    const digit = val.replace(/[^0-9]/g, "").slice(-1);
    const next = [...digits];
    next[i] = digit;
    setDigits(next);
    if (digit && i < CODE_LEN - 1) {
      inputsRef.current[i + 1]?.focus();
    }
    // Auto-submit when the last box gets filled OR we have exactly 4 digits
    // and the user presses Enter later.
    if (digit && next.every((d, idx) => idx < CODE_LEN ? true : d)) {
      // no-op — submission happens on explicit Enter or button click
    }
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        // Clear current box
        const next = [...digits];
        next[i] = "";
        setDigits(next);
      } else if (i > 0) {
        // Move to previous
        inputsRef.current[i - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      inputsRef.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < CODE_LEN - 1) {
      inputsRef.current[i + 1]?.focus();
    } else if (e.key === "Enter") {
      submitCode(digits.join(""));
    }
  };

  const handlePaste = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    const clean = (paste || "").replace(/[^0-9]/g, "").slice(0, CODE_LEN);
    if (!clean) return;
    e.preventDefault();
    const next = Array(CODE_LEN).fill("");
    clean.split("").forEach((c, i) => { next[i] = c; });
    setDigits(next);
    const lastFilled = Math.min(clean.length, CODE_LEN) - 1;
    inputsRef.current[Math.min(lastFilled + 1, CODE_LEN - 1)]?.focus();
    if (clean.length >= 4) submitCode(clean);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitCode(digits.join(""));
  };

  const isCodeReady = digits.filter((d) => d).length >= 4;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex" data-testid="login-page">
      {/* Left brand column */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden border-r border-white/10">
        <div className="absolute inset-0 hq-grid opacity-40"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[color:var(--hq-cyan)]/5 blur-3xl"></div>
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <Link to="/" className="inline-flex items-center gap-2.5" data-testid="login-brand">
            <BrandMark size={32} animated />
            <span className="font-display font-medium tracking-tight text-sm">EMRKT.CH</span>
          </Link>
          <div>
            <h2 className="font-display font-light text-4xl tracking-tight leading-tight max-w-md">
              {t(lang, "tagline")}
            </h2>
            <p className="mt-4 text-white/50 text-sm max-w-md">{t(lang, "hero_body")}</p>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/30">
            {t(lang, "footer_note")}
          </div>
        </div>
      </div>

      {/* Right form column */}
      <div className="flex-1 flex flex-col">
        <header className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-white/10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-200 text-sm" data-testid="back-to-landing">
            <ArrowLeft size={14} /> home
          </Link>
          <LanguageSwitcher />
        </header>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-sm">
            <div className="mb-8 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[color:var(--hq-cyan)]/10 border border-[color:var(--hq-cyan)]/30 mb-4">
                <KeyRound size={22} className="text-[color:var(--hq-cyan)]" />
              </div>
              <h1 className="font-display font-light text-3xl tracking-tight">
                Código de acesso
              </h1>
              <p className="mt-2 text-white/50 text-sm">
                Digita o teu código numérico para entrar no painel EMRKT.CH.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="code-login-form">
              <div className="flex items-center justify-center gap-2" onPaste={handlePaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputsRef.current[i] = el; }}
                    data-testid={`code-digit-${i}`}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onFocus={(e) => e.target.select()}
                    aria-label={`Dígito ${i + 1}`}
                    disabled={busy}
                    className="w-12 h-14 text-center font-mono text-2xl bg-black/60 border border-white/15 rounded-lg focus:border-[color:var(--hq-cyan)] focus:outline-none focus:ring-2 focus:ring-[color:var(--hq-cyan)]/20 transition-all disabled:opacity-40"
                  />
                ))}
              </div>

              <button
                data-testid="code-submit-btn"
                type="submit"
                disabled={busy || !isCodeReady}
                className="hq-btn hq-btn-primary w-full py-3 disabled:opacity-40"
              >
                {busy ? "A verificar…" : "Entrar"}
              </button>
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-white/10"></div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">or</span>
              <div className="h-px flex-1 bg-white/10"></div>
            </div>

            <button
              data-testid="login-google-btn"
              onClick={handleGoogle}
              className="hq-btn hq-btn-ghost w-full py-3"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t(lang, "login_google")}
            </button>

            <p className="mt-6 text-center text-sm">
              <span className="inline-flex items-center gap-1 text-white/40" data-testid="public-locked-note">
                <Lock size={10} /> Acesso restrito ao operador
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
